import type { TableFrameColumn } from '../TableFrame/context'

/** Must equal the Sass breakpoint `desktop` (ADR 0012). */
const DESKTOP_QUERY = '(min-width: 1152px)'

function remToPx(rem: number): number {
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
  return (Number.isFinite(root) && root > 0 ? root : 16) * rem
}

/** Narrowest a column-aligned filter may be, and the width of one without a column. */
export const ALIGNED_MIN_REM = 9
export const FREE_REM = 10

interface Placed {
  el: HTMLElement
  start: number
  end: number
}

function reset(root: HTMLElement, fields: HTMLElement, items: HTMLElement[]) {
  delete root.dataset.aligned
  fields.style.removeProperty('grid-template-columns')
  for (const item of items) {
    item.style.removeProperty('grid-column')
    item.style.removeProperty('grid-row')
  }
}

/**
 * Line each filter up with its table column (ADR 0023): a filter starts where
 * its column's content starts and runs to the next filtered column (or, the
 * last one, to its own column's right edge, at least a filter wide). Filters
 * without a column follow at a fixed width; the actions take what is left at
 * the end. When a filter
 * would be narrower than ALIGNED_MIN_REM, the order is not left to right, or
 * anything does not fit, the bar keeps its responsive grid instead.
 *
 * Returns whether the bar is aligned.
 */
export function alignFilters(
  root: HTMLElement,
  fields: HTMLElement,
  actions: HTMLElement | null,
  columns: readonly TableFrameColumn[] | null | undefined
): boolean {
  const items = Array.from(fields.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement
  )
  reset(root, fields, items)
  if (!columns || columns.length === 0 || items.length === 0) return false
  if (typeof window.matchMedia !== 'function' || !window.matchMedia(DESKTOP_QUERY).matches) {
    return false
  }

  const box = root.getBoundingClientRect()
  const gap = Number.parseFloat(getComputedStyle(fields).columnGap) || remToPx(1)
  const min = remToPx(ALIGNED_MIN_REM)
  const free = remToPx(FREE_REM)
  const byKey = new Map(columns.map((column) => [column.key, column]))
  const columnOf = (el: HTMLElement) => {
    const key = el.dataset.filterColumn
    return key === undefined ? undefined : byKey.get(key)
  }
  const aligned = items.filter((el) => columnOf(el) !== undefined)
  const others = items.filter((el) => columnOf(el) === undefined)
  if (aligned.length === 0) return false

  const placed: Placed[] = []
  for (const [i, el] of aligned.entries()) {
    const column = columnOf(el)
    if (!column) return false
    // Never left of the bar: the first column can start inside its padding.
    const start = Math.max(0, column.left - box.left)
    const next = aligned[i + 1]
    const nextColumn = next ? columnOf(next) : undefined
    // The last aligned filter covers its column, or more when the column is
    // narrower than a filter (an icon column): it runs over the next ones.
    const end = nextColumn
      ? nextColumn.left - box.left - gap
      : Math.max(column.right - box.left, start + min)
    const previous = placed.at(-1)
    if (previous && start < previous.end + gap - 0.5) return false
    if (end - start < min) return false
    placed.push({ el, start, end })
  }
  let cursor = (placed.at(-1)?.end ?? 0) + gap
  for (const el of others) {
    placed.push({ el, start: cursor, end: cursor + free })
    cursor += free + gap
  }
  const actionsWidth = actions ? actions.getBoundingClientRect().width : 0
  if (cursor + actionsWidth > box.width + 0.5) return false

  const tracks: string[] = []
  let previousEnd = 0
  for (const [i, p] of placed.entries()) {
    tracks.push(`${String(Math.max(0, p.start - previousEnd))}px`, `${String(p.end - p.start)}px`)
    previousEnd = p.end
    p.el.style.setProperty('grid-column', String(i * 2 + 2))
    p.el.style.setProperty('grid-row', '1')
  }
  tracks.push('minmax(0, 1fr)')
  fields.style.setProperty('grid-template-columns', tracks.join(' '))
  root.dataset.aligned = ''
  return true
}
